import type { ReactNode } from "react";
import { IconChevron, IconProof } from "./Icons";
import { ThemeSwitch } from "./ThemeSwitch";

export function Shell({
  context,
  right,
  nav,
  tabbar,
  feedback,
  onHome,
  onBack,
  backLabel = "Назад",
  children,
}: {
  /**
   * Имя экрана. С 24 сентября 2026 оно здесь ЕДИНСТВЕННОЕ: заголовок той же
   * строкой в теле страницы («Графики» под словом «Лаборатория», «Личные
   * данные» под «Личные данные») говорил то же самое второй раз и отодвигал
   * содержимое вниз на свою высоту.
   */
  context?: ReactNode;
  right?: ReactNode;
  /**
   * Переходы. У ученика — содержимое боковой колонки (src/ui/AppNav.tsx), у
   * взрослого — ряд значков в шапке (src/screens/Tutor.tsx); какая раскладка,
   * решает `tabbar`. Слот, а не сам ряд: инструментам автора (`?map=1`,
   * `?control=1`) и экрану без ключа переходы не нужны вовсе.
   */
  nav?: ReactNode;
  /**
   * Нижняя панель переходов на телефоне (src/ui/AppNav.tsx, `TabBar`). Стоит
   * вне боковой колонки: та на узком экране скрыта целиком.
   */
  tabbar?: ReactNode;
  /**
   * Кнопка обратной связи (src/ui/Feedback.tsx). Тоже слот: ей нужен ключ того,
   * кто пишет, и место, где он сейчас, — это знает экран, а не шапка. Без
   * ключа (инструменты автора, экран «ссылка без ключа») писать некому.
   */
  feedback?: ReactNode;
  /**
   * Имя продукта ведёт на набор дня. Появилось 11 сентября 2026: с экрана
   * задачи вернуться было нельзя вообще ничем, и единственным выходом
   * оставалась кнопка «назад» браузера — а роутера нет, и она уводит из
   * приложения целиком.
   *
   * Решение «на задаче переходов нет» это не отменяет: запрет был про ссылку
   * «Прогресс» над недорешённой задачей — приглашение заняться чем-то другим.
   * Имя продукта ничего не предлагает; это выход, а не приманка, и стоит там,
   * где его ищут не глядя.
   */
  onHome?: () => void;
  /**
   * Выход с экрана — шевроном слева от его имени. Один приём на все экраны:
   * до этого «назад» стоял то внутри рамки лаборатории, то над числами
   * «Прогресса», то ссылкой в атласе, и на каждом экране искался заново.
   *
   * Ведёт откуда пришли (с задачи — в задачу), в отличие от имени продукта,
   * которое ведёт в набор дня всегда. Экранам потока его не дают: с зачёта и
   * с итога дня уходят их собственной концовкой, а не шевроном в углу.
   */
  onBack?: () => void;
  /** Куда ведёт шеврон — для подсказки и скринридера. */
  backLabel?: string;
  children: ReactNode;
}) {
  const wordmark = onHome ? (
    <button className="wordmark wordmark--home" onClick={onHome} title="К набору дня">
      Proof
    </button>
  ) : (
    <div className="wordmark">Proof</div>
  );
  const topbar = (
    <header className="topbar">
      <div className="topbar__inner">
        {/* В раскладке с боковой колонкой имя продукта стоит там; в шапке
            оно остаётся для телефона, где колонки нет. */}
        <span className="topbar__brand">{wordmark}</span>
        {context ? (
          <>
            <span className="topbar__rule" />
            {onBack ? (
              <button
                type="button"
                className="topbar__back"
                onClick={onBack}
                title={backLabel}
                aria-label={backLabel}
              >
                <span className="back__chev">
                  <IconChevron size={18} width={2.2} />
                </span>
              </button>
            ) : null}
            <div className="topbar__context">{context}</div>
          </>
        ) : null}
        <div className="topbar__spacer" />
        {right ? <div className="topbar__right">{right}</div> : null}
        {/* Ряд переходов в шапке — у страницы взрослого (src/screens/Tutor.tsx):
            боковая колонка с нижней панелью пока только у ученика. */}
        {tabbar ? null : nav ?? null}
        {feedback ?? null}
        {/* Оформление у ученика — в боковой колонке и в «Ещё», у остальных
            экранов здесь. */}
        {tabbar ? null : <ThemeSwitch />}
      </div>
    </header>
  );

  // Боковая колонка — у экранов ученика, их узнаём по нижней панели. Взрослому,
  // инструментам автора и экрану без ключа остаётся прежняя раскладка.
  if (!tabbar) {
    return (
      <div className="shell">
        {topbar}
        <main className="page">{children}</main>
      </div>
    );
  }

  /*
    Раскладка teen-english (решение 28 октября 2026): слева колонка со знаком
    продукта и переходами с подписями, справа шапка экрана и сама страница.
    На телефоне колонка скрыта, переходы уходят в нижнюю панель.
  */
  return (
    <div className="shell shell--side">
      <aside className="side" aria-label="Proof">
        <div className="side-brand">
          <span className="side-logo" aria-hidden="true">
            <IconProof size={22} />
          </span>
          {/* Слоган — только здесь и на первом экране (выбор цели): на
              экранах задач фраза, висящая везде, становится обоями. */}
          <div className="side-brand__name">
            {wordmark}
            <div className="side-tagline">Знаешь? Докажи</div>
          </div>
        </div>
        {nav}
      </aside>
      <div className="shell__main">
        {topbar}
        <main className="page">{children}</main>
      </div>
      {tabbar ?? null}
    </div>
  );
}
